import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { ConditionGroup } from 'api/models/FieldTemplate';
import { useFormik } from 'formik';
import useConditionData from 'hooks/useConditionData';
import { useRecoilState, useRecoilValue } from 'recoil';
import { dialogState, formTemplateState, layoutSelector } from 'state';
import ConditionEditor from './AddFieldTemplate/components/ConditionConfig/ConditionEditor';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import { produce } from 'immer';
import { set } from 'object-path';
import { Box, useMediaQuery } from '@mui/system';
import { useTranslation } from 'react-i18next';

type EditSelectableTableFieldFormikValues = {
  isVisible?: ConditionGroup;
};

export default function EditSelectableTableFieldDialog() {
  const { loading } = useConditionData();
  const [{ data }, closeDialog] = useRecoilState(dialogState);
  const { layoutItem: item, path } = data;
  const formTemplate = useRecoilValue(formTemplateState);
  const disabled = formTemplate?.status === FormTemplateStatus.Published;
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const formik = useFormik<EditSelectableTableFieldFormikValues>({
    initialValues: {
      isVisible: item.layoutOptions?.isVisible,
    },
    onSubmit(values, { resetForm }) {
      const items = produce(layout, draft => {
        set(draft, `${path}.layoutOptions.isVisible`, values.isVisible);
      });
      setLayout(items);

      resetForm();
      closeDialog({
        isOpen: false,
      });
    },
  });

  return (
    <Box width={isMobile ? 'auto' : '900px'}>
      <DialogTitle>{t('dialogs.editSelectableTableField.title')}</DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <ConditionEditor
            onChange={group => formik.setFieldValue('isVisible', group)}
            rootGroup={formik.values.isVisible}
            disabled={disabled}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            closeDialog({
              isOpen: false,
            });
          }}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          disabled={!formik.isValid || disabled}
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
        >
          {t('general.save')}
        </Button>
      </DialogActions>
    </Box>
  );
}
